import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Award, FileCheck, Upload, CheckCircle2, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import CitySelect from '../components/CitySelect';

/**
 * /consultant/signup — 3-step CA / Tax Consultant onboarding flow.
 *   Step 1: Profile signup
 *   Step 2: 5-MCQ knowledge test (3/5 to pass)
 *   Step 3: KYC document upload (PAN, Aadhaar, CA Cert, Cheque, Selfie)
 * After step 3 → status 'kyc_submitted' → awaiting admin approval.
 *
 * Privacy / anti-bypass: this is the PUBLIC onboarding page; no chat or contact
 * sharing happens here. Once approved, consultant logs in at /client-portal.
 */

const StepDot = ({ n, label, step }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 28, height: 28, borderRadius: 999, background: step >= n ? '#FF6B35' : '#E2E8F0', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.78rem' }}>{step > n ? <CheckCircle2 size={16} /> : n}</div>
    <span style={{ fontSize: '0.82rem', fontWeight: step === n ? 800 : 500, color: step >= n ? '#0F172A' : '#94A3B8' }}>{label}</span>
  </div>
);

const Step1Basic = ({ form, setForm, submitting, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="step1-form">
    <h3 style={{ margin: '0 0 1.25rem', fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>Tell us about yourself</h3>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
      <div><label className="label-text">Full Name *</label><input className="input-field" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="cs-name" /></div>
      <div><label className="label-text">Email *</label><input type="email" className="input-field" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cs-email" /></div>
      <div><label className="label-text">Phone (10 digit) *</label><input className="input-field" required maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })} data-testid="cs-phone" /></div>
      <div><label className="label-text">City</label><CitySelect value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="cs-city" /></div>
      <div><label className="label-text">Qualification *</label>
        <select className="input-field" value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} data-testid="cs-qual">
          {['CA', 'CS', 'CMA', 'CA Inter', 'B.Com + GST Practitioner', 'M.Com', 'Other'].map((q) => <option key={q}>{q}</option>)}
        </select>
      </div>
      <div><label className="label-text">ICAI Membership No.</label><input className="input-field" value={form.icai_membership_no} onChange={(e) => setForm({ ...form, icai_membership_no: e.target.value })} data-testid="cs-icai" /></div>
      <div><label className="label-text">Experience (years)</label><input type="number" min="0" max="50" className="input-field" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value) || 0 })} data-testid="cs-exp" /></div>
      <div><label className="label-text">Specialisation</label><input className="input-field" placeholder="ITR, GST, Audit, …" value={form.specialisation} onChange={(e) => setForm({ ...form, specialisation: e.target.value })} data-testid="cs-spec" /></div>
    </div>
    <button type="submit" disabled={submitting} className="primary-button" style={{ marginTop: '1.5rem', width: '100%' }} data-testid="cs-next-1">
      {submitting ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : 'Continue → Take Quick Test'}
    </button>
  </form>
);

const Step2Test = ({ questions, answers, setAnswers, testResult, submitting, onSubmit }) => (
  <div data-testid="step2-test">
    <h3 style={{ margin: '0 0 0.5rem', fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>Quick knowledge check — 5 questions</h3>
    <p style={{ color: '#64748B', fontSize: '0.88rem', margin: '0 0 1.5rem' }}>Score 3 of 5 to proceed to KYC. Single-choice. ~2 min.</p>
    {questions.map((q, i) => (
      <div key={q.id} style={{ marginBottom: '1.25rem', padding: '1rem 1.1rem', background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0' }}>
        <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 8, fontSize: '0.92rem' }}>Q{i + 1}. {q.question}</div>
        {q.options.map((opt, oi) => (
          <label key={oi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: answers[q.id] === oi ? '#FFF7ED' : 'transparent', fontSize: '0.88rem' }}>
            <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))} data-testid={`cs-q-${q.id}-${oi}`} />
            {opt}
          </label>
        ))}
      </div>
    ))}
    {testResult && !testResult.passed && (
      <div style={{ padding: '10px 12px', background: '#FEF2F2', color: '#991B1B', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}><AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> Scored {testResult.score}/{testResult.total}. Please review and try again.</div>
    )}
    <button onClick={onSubmit} disabled={submitting} className="primary-button" style={{ width: '100%' }} data-testid="cs-submit-test">
      {submitting ? <><Loader2 size={16} className="animate-spin" /> Grading…</> : 'Submit Answers'}
    </button>
  </div>
);

const KYC_FIELDS = [
  { key: 'pan_url', label: 'PAN Card' },
  { key: 'aadhaar_url', label: 'Aadhaar Card' },
  { key: 'ca_cert_url', label: 'CA / ICAI Membership Certificate' },
  { key: 'cheque_url', label: 'Cancelled Bank Cheque' },
  { key: 'selfie_url', label: 'Selfie (with PAN held in hand)' },
];

const Step3Kyc = ({ kyc, onUpload, submitting, onSubmit }) => (
  <div data-testid="step3-kyc">
    <h3 style={{ margin: '0 0 0.5rem', fontFamily: 'Outfit, sans-serif', color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}><FileCheck size={18} style={{ color: '#FF6B35' }} /> KYC documents</h3>
    <p style={{ color: '#64748B', fontSize: '0.88rem', margin: '0 0 1.5rem' }}>Upload clear photos / PDFs. All docs are confidential and reviewed by admin only.</p>
    {KYC_FIELDS.map((d) => (
      <div key={d.key} style={{ marginBottom: '1rem', padding: '12px 14px', border: '1px dashed #E2E8F0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.88rem' }}>{d.label} *</div>
          {kyc[d.key] && <div style={{ fontSize: '0.72rem', color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={12} /> Uploaded</div>}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#FF6B35', color: 'white', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
          <Upload size={14} /> {kyc[d.key] ? 'Re-upload' : 'Upload'}
          <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => onUpload(d.key, e.target.files?.[0])} data-testid={`cs-upload-${d.key}`} />
        </label>
      </div>
    ))}
    <button onClick={onSubmit} disabled={submitting} className="primary-button" style={{ width: '100%', marginTop: '1rem' }} data-testid="cs-submit-kyc">
      {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : 'Submit for Verification'}
    </button>
  </div>
);

const Step4Done = ({ appId }) => (
  <div style={{ textAlign: 'center' }} data-testid="step4-done">
    <CheckCircle2 size={56} style={{ color: '#10B981' }} />
    <h3 style={{ margin: '12px 0 8px', fontFamily: 'Outfit, sans-serif', color: '#0F172A', fontSize: '1.4rem' }}>You&apos;re done! 🎉</h3>
    <p style={{ color: '#475569', margin: '0 0 1.25rem' }}>Your application is under review. We&apos;ll email your login credentials within 24-48 hours of approval.</p>
    <div style={{ background: '#F8FAFC', padding: '12px 14px', borderRadius: 10, fontSize: '0.82rem', color: '#475569', textAlign: 'left' }}>
      <strong>Application reference:</strong> <span style={{ fontFamily: 'monospace', color: '#FF6B35' }}>{appId?.slice(0, 12)}</span>
    </div>
    <div style={{ marginTop: '1.5rem', padding: '12px 14px', background: '#FEF3C7', borderRadius: 10, color: '#92400E', fontSize: '0.82rem', textAlign: 'left' }}>
      <Lock size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /><strong>Important:</strong> Once active, you&apos;ll be assigned a random display name (e.g., &ldquo;CA Aarya 137&rdquo;) to protect your privacy. You can chat with clients only via the Arthik portal — sharing personal phone/email/UPI is strictly prohibited.
    </div>
  </div>
);

const ConsultantSignupPage = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [appId, setAppId] = useState(null);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', city: '', qualification: 'CA',
    icai_membership_no: '', experience_years: 1, specialisation: '',
  });
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [kyc, setKyc] = useState({ pan_url: '', aadhaar_url: '', ca_cert_url: '', cheque_url: '', selfie_url: '' });

  useEffect(() => {
    if (step === 2 && questions.length === 0) {
      axios.get(`${API}/consultant/test-questions`).then((r) => setQuestions(r.data.questions || []));
    }
  }, [step, questions.length]);

  const signup = async (e) => {
    e?.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.qualification) {
      toast.error('Please fill all required fields'); return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) {
      toast.error('Phone must be 10 digits'); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/consultant/signup`, form);
      setAppId(r.data.application_id);
      toast.success('Profile saved — now take the quick test');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not signup');
    } finally { setSubmitting(false); }
  };

  const submitTest = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions'); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/consultant/submit-test`, { application_id: appId, answers });
      setTestResult(r.data);
      if (r.data.passed) { toast.success(`Passed! ${r.data.score}/${r.data.total}`); setTimeout(() => setStep(3), 1500); }
      else { toast.error(`Scored ${r.data.score}/${r.data.total}. You can retry.`); setAnswers({}); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Test submission failed'); }
    finally { setSubmitting(false); }
  };

  const uploadDoc = async (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/storage/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setKyc((k) => ({ ...k, [field]: r.data.url }));
      toast.success(`${field.replace('_url', '').toUpperCase()} uploaded`);
    } catch (err) { toast.error('Upload failed'); }
  };

  const submitKyc = async () => {
    for (const k of ['pan_url', 'aadhaar_url', 'ca_cert_url', 'cheque_url', 'selfie_url']) {
      if (!kyc[k]) { toast.error(`Please upload ${k.replace('_url', '').toUpperCase()}`); return; }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultant/submit-kyc`, { application_id: appId, ...kyc });
      setStep(4);
    } catch (err) { toast.error(err.response?.data?.detail || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 100%)', padding: '3rem 1.5rem' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Award size={42} style={{ color: '#FF6B35' }} />
          <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '2rem', fontWeight: 800, color: '#0F172A', margin: '12px 0 6px' }}>Join Arthik as a Tax Consultant</h1>
          <p style={{ color: '#475569', margin: 0 }}>Earn <strong>70%</strong> of every case · Round-robin auto-assignment · Free consultant tools · Verified PAN India clients</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: 12 }}>
          <StepDot n={1} label="Profile" step={step} />
          <span style={{ width: 30, height: 2, background: '#E2E8F0' }} />
          <StepDot n={2} label="Knowledge Test" step={step} />
          <span style={{ width: 30, height: 2, background: '#E2E8F0' }} />
          <StepDot n={3} label="KYC" step={step} />
        </div>

        <div style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.06), 0 0 0 1px #FED7AA40' }} data-testid="consultant-signup-card">
          {step === 1 && <Step1Basic form={form} setForm={setForm} submitting={submitting} onSubmit={signup} />}
          {step === 2 && <Step2Test questions={questions} answers={answers} setAnswers={setAnswers} testResult={testResult} submitting={submitting} onSubmit={submitTest} />}
          {step === 3 && <Step3Kyc kyc={kyc} onUpload={uploadDoc} submitting={submitting} onSubmit={submitKyc} />}
          {step === 4 && <Step4Done appId={appId} />}
        </div>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.78rem', color: '#94A3B8' }}>
          Already a verified consultant? <a href="/client-portal" style={{ color: '#FF6B35', fontWeight: 700 }}>Login at /client-portal →</a>
        </p>
      </div>
    </div>
  );
};

export default ConsultantSignupPage;
